import React, { useMemo } from "react"
import HyperDiv from "components/ProjectNonSpecificComponents/Elements/HyperDiv/HyperDiv"
import { useTranslation } from "react-i18next"
import { observer } from "mobx-react"
import FormField from "../FormField"
import Whitespace from "components/ProjectNonSpecificComponents/Elements/Whitespace"
import useScreenType from "components/ProjectNonSpecificComponents/Hooks/useScreenType"
import { usePTKMStore } from "components/ProjectSpecificComponents/Contexts/PTKMStoreContext"
import {
	isReactSelectOptionArray,
	isReactSelectSingleOption,
	TReactSelectOption,
} from "components/ProjectSpecificComponents/Interfaces/ExternalComponentsInterfaces"
import Select from "components/ProjectSpecificComponents/Elements/Global/atoms/Select"
import { defaultStatusFieldFormData } from "components/ProjectSpecificComponents/Stores/PTKMStore/defaults"
import { REACT_SELECT_FAIL_FALLBACK_OPTIONS } from "components/ProjectSpecificComponents/Constants/GLOBAL_CONSTANTS"
import getStoreProperty from "components/ProjectNonSpecificComponents/DevTools/Functions/getStoreProperty"
import { PTKM_REQ_CLASSNAME } from "./ActivatePTKMForm"

interface Props {}

export const StatusesForm = observer(({}: Props) => {
	const ptkmStore = usePTKMStore()
	const { isMobile } = useScreenType()
	const { t } = useTranslation()

	const statusOptions: TReactSelectOption[] | undefined = useMemo(() => {
		/** Gathers all options used for status select field */
		// If main field is selected
		if (!ptkmStore.mainFieldOptions?.picklist) return undefined
		var disabledUsedOptions: TReactSelectOption[] | undefined

		ptkmStore.mainFieldOptions.picklist.map((f) => {
			if (f?.value == ptkmStore.ptkmConfig.status.statusPickList.fieldId) {
				if (!f.extraProp) return undefined
				const options: TReactSelectOption[] = [...JSON.parse(f.extraProp)]

				disabledUsedOptions = options.map((option) => ({
					...option,
					// isDisabled: JSON.stringify(ptkmStore.ptkmConfig.status).includes(
					// 	`"value":"${option.value}"`
					// ),
				}))
			}
		})
		return disabledUsedOptions
	}, [ptkmStore.mainFieldOptions, ptkmStore.ptkmConfig.status])

	const clearAfterMainFieldChange = () => {
		ptkmStore.editPTKMconfig("status", {
			startStatus: [],
			pauseStatus: [],
			krStopStatus: [],
			rlaStopStatus: [],
			startWhenFieldHasValues: defaultStatusFieldFormData,
		})
	}

	return (
		<HyperDiv direction="column" padding=".5rem 0" spaceBetweenElems="2.25rem">
			<HyperDiv className="fill-cter" width={isMobile ? undefined : 300} padding="1rem">
				<FormField
					label={`${t("forms.field-label.configuration-status-jama")}*`}
					placeholderValue=""
					editable
					direction={isMobile ? "Horizontal" : "Vertical"}
				>
					<Select
						className={PTKM_REQ_CLASSNAME}
						valueKey={ptkmStore.ptkmConfig.status.statusPickList?.fieldId}
						options={ptkmStore.mainFieldOptions.picklist}
						onChange={(selectedOption) => {
							ptkmStore.editPTKMconfig("status", {
								statusPickList: isReactSelectSingleOption(selectedOption)
									? {
											fieldId: selectedOption?.value,
											name: selectedOption?.label,
									  }
									: defaultStatusFieldFormData,
							})
							clearAfterMainFieldChange()
						}}
						placeholder=""
					/>
				</FormField>
			</HyperDiv>
			{statusOptions && (
				<HyperDiv
					className="fill-cter"
					width={isMobile ? undefined : 300}
					style={{
						backgroundColor: "var(--light-light-gray)",
					}}
					padding="1rem"
				>
					<FormField
						label={`${t("forms.field-label.status-time-start")}*`}
						placeholderValue=""
						editable
						direction={isMobile ? "Horizontal" : "Vertical"}
					>
						<Select
							className={PTKM_REQ_CLASSNAME}
							value={ptkmStore.ptkmConfig.status.startStatus}
							options={statusOptions}
							isMulti
							onChange={(selectedOption) => {
								if (!isReactSelectOptionArray(selectedOption)) return
								ptkmStore.editPTKMconfig("status", {
									startStatus: [...selectedOption],
								})
							}}
							placeholder=""
						/>
					</FormField>
					<Whitespace x="1" />
					<FormField
						label={`${t("forms.field-label.status-time-pause")}`}
						placeholderValue=""
						editable
						direction={isMobile ? "Horizontal" : "Vertical"}
					>
						<Select
							value={ptkmStore.ptkmConfig.status.pauseStatus}
							options={statusOptions}
							isMulti
							onChange={(selectedOption) => {
								if (!isReactSelectOptionArray(selectedOption)) return
								ptkmStore.editPTKMconfig("status", {
									pauseStatus: [...selectedOption],
								})
							}}
							placeholder=""
						/>
					</FormField>
					<Whitespace x="1" />
					<FormField
						label={`${t("forms.field-label.status-time-stop")} (RLA)*`}
						placeholderValue=""
						editable
						direction={isMobile ? "Horizontal" : "Vertical"}
					>
						<Select
							className={PTKM_REQ_CLASSNAME}
							value={ptkmStore.ptkmConfig.status.rlaStopStatus}
							options={statusOptions}
							isMulti
							onChange={(selectedOption) => {
								if (!isReactSelectOptionArray(selectedOption)) return
								ptkmStore.editPTKMconfig("status", {
									rlaStopStatus: [...selectedOption],
								})
							}}
							placeholder=""
						/>
					</FormField>
					<Whitespace x="1" />

					<FormField
						label={`${t("forms.field-label.status-time-stop")} (KR)`}
						placeholderValue=""
						editable
						direction={isMobile ? "Horizontal" : "Vertical"}
					>
						<Select
							value={ptkmStore.ptkmConfig.status.krStopStatus}
							options={statusOptions}
							isMulti
							onChange={(selectedOption) => {
								if (!isReactSelectOptionArray(selectedOption)) return
								ptkmStore.editPTKMconfig("status", {
									krStopStatus: [...selectedOption],
								})
							}}
							placeholder=""
						/>
					</FormField>
					<Whitespace x="1" />
					<FormField
						label={t("forms.field-label.status-time-start-any-value")}
						placeholderValue=""
						editable
						direction={isMobile ? "Horizontal" : "Vertical"}
					>
						<Select
							valueKey={ptkmStore.ptkmConfig.status.startWhenFieldHasValues.fieldId}
							options={ptkmStore.mainFieldOptions.all}
							onChange={(selectedOption) => {
								ptkmStore.editPTKMconfig("status", {
									startWhenFieldHasValues: isReactSelectSingleOption(selectedOption)
										? {
												fieldId: selectedOption?.value,
												name: selectedOption?.label,
										  }
										: defaultStatusFieldFormData,
								})
							}}
							placeholder=""
						/>
					</FormField>
				</HyperDiv>
			)}
		</HyperDiv>
	)
})

export default StatusesForm
